/**
 * Workspace tab — plan §11 item 4 (the grid, §8.1, with a right-hand panel of
 * three tabs: Changes, Notes, and a slot reserved for the assistant; toolbar
 * actions Apply rate card, Apply previous study, Accept all, Export; lock
 * indicator). §13.6 WP-55 replaces WP-03's placeholder.
 *
 * ROUTE AND VERSION SELECTION (this WP's brief). The route is the existing
 * `/contracts/workspace` with NO path params (ContractsApp.tsx is a hot
 * file this WP may not touch) — the tab reads its target from the query
 * string with `useSearchParams` (react-router-dom 6, the FaxConsole.tsx
 * pattern): `?workspaceId=<uuid>` opens that workspace directly
 * (`GET /workspaces/:id`); `?versionId=<uuid>&mode=edit_in_place|own_version`
 * opens or re-opens over that version (`POST /workspaces`) and rewrites the
 * query to `?workspaceId=` with `replace: true` so a reload is idempotent.
 * With neither param, `WorkspaceOpener` renders.
 *
 * THE SHEETS IDENTITY RULE (WP-50 README, WP-51 README, ledger WP-50 line):
 * `sheets` lives in ONE `useState`, seeded once per workspace id from the
 * open/get payload's `workspace.grid.sheets`, and is NEVER set from a
 * `PUT /cells` response. It IS deliberately replaced after a server-side
 * structural or proposal change — Apply rate card, Apply previous study,
 * accept, reject, export — by re-`GET`ting the workspace (`reseedAfterServerChange`
 * below), with a toast saying the in-grid undo history was cleared.
 *
 * THE LOCK POLLS, IT DOES NOT SUBSCRIBE (this WP's brief, [OWNER]):
 * `frontend/src/lib/realtimeTransport.ts` has no `contracts:site:<siteId>`
 * channel subscription today, so the browser cannot receive WP-51's
 * `contracts.lock.changed` event. One 30 s interval instead (§8.5's own
 * cadence): `POST .../heartbeat` while editable, `GET /workspaces/:id`
 * (ignoring its `grid`, per the identity rule) while not. A
 * `subscribeContractsSite` in realtimeTransport.ts (outside this WP's
 * paths) would replace the poll with a push and needs no change here beyond
 * swapping the effect.
 *
 * THE TWO APPLY ROUTES AND THE EXPORT ROUTE ARE MOUNTED (see
 * api/workspace.ts's header: commit 2bf341e3 "APPLY-ROUTES" for the apply
 * routes, `routes/contracts.js`'s `createExportsRouter` line for export).
 * That does not make loud failure optional — a 404 here is now typically an
 * unknown or cross-site workspace/version id (§4.9), not an absent route.
 * Every dialog surfaces any failure loudly (G13) — a toast here plus its
 * own inline error.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, Loader2 } from 'lucide-react';
import { PageHeader, EmptyState, ToastProvider, useToast, outlineBtnCls } from '../../../ui';
import { BudgetGrid } from '../../components/grid';
import type {
  BudgetGridHandle,
  CellFocusEvent,
  GridChangeEvent,
  GridSheet,
  InsertColumnEvent,
  InsertRowEvent,
} from '../../components/grid';
import type { NotesPanelSelection } from '../../components/notesPanel';
import { useAuth } from '../../../contexts/AuthContext';
import { useContracts } from '../../ContractsProvider';
import {
  acceptWorkspaceChanges,
  describeWorkspaceApiError,
  getVersion,
  getWorkspace,
  heartbeatWorkspace,
  isRetryableWorkspaceError,
  listWorkspaceChanges,
  lockHolderFromError,
  lockWorkspace,
  openWorkspace,
  putWorkspaceCells,
  rejectWorkspaceChanges,
  unlockWorkspace,
  type ApplyPreviousStudyResult,
  type ApplyRateCardResult,
  type ContractsVersion,
  type ExportPathMode,
  type ExportWorkspaceResult,
  type WorkspaceChange,
  type WorkspaceCounts,
  type WorkspaceGetResult,
  type WorkspaceHolder,
  type WorkspaceLockInfo,
  type WorkspaceMode,
  type WorkspaceOpenResult,
  type WorkspaceRow,
  type WorkspaceSummary,
} from '../../api/workspace';
import { WorkspaceOpener } from './WorkspaceOpener';
import { Toolbar } from './Toolbar';
import { LockIndicator } from './LockIndicator';
import { SidePanel, type PanelTab } from './SidePanel';
import { ApplyRateCardDialog } from './ApplyRateCardDialog';
import { ApplyPreviousStudyDialog } from './ApplyPreviousStudyDialog';
import { ExportDialog } from './ExportDialog';

const FALLBACK_WIDTH = 960;
const FALLBACK_HEIGHT = 420;
const DEFAULT_HEARTBEAT_SECONDS = 30;

// R-1 (§16.7): the failed-PUT retry schedule for one flush sequence. Attempt
// 1 is the original send; attempt n+1 waits FLUSH_RETRY_DELAYS_MS[n-1], so a
// save that never succeeds is over in ~13s and four requests. The ceiling is
// DERIVED from the array on purpose — a reviewer cannot change one without
// the other. No jitter, no runtime-computed backoff: §8.5 gives a workspace
// one editor, so there is no herd to spread, and jest's fake timers need an
// exact, deterministic schedule to drive.
const FLUSH_RETRY_DELAYS_MS = [1000, 3000, 9000];
const FLUSH_MAX_ATTEMPTS = 1 + FLUSH_RETRY_DELAYS_MS.length;

/**
 * One flush's frozen payload (§16.7 R-4): minted at the freeze and NEVER
 * inside the schedule, so every attempt of one sequence — the original send
 * and every automatic or manual retry — carries the identical clientBatchId,
 * changes and sheets.
 */
interface FrozenFlushPayload {
  clientBatchId: string;
  events: GridChangeEvent[];
  includeSheets: boolean;
  sheets?: GridSheet[];
}

/** Parses an A1 ref like "AB12" into 1-based { row, col }. Not exported by components/grid (types.ts has no such helper) — small enough to keep local. */
function parseCellRef(ref: string): { row: number; col: number } | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  let col = 0;
  for (const ch of m[1].toUpperCase()) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  const row = Number(m[2]);
  if (!Number.isFinite(row) || row < 1) return null;
  return { row, col };
}

/** Merge a partial echo (accept/reject/apply responses only carry the rows THEY touched) into the full list this tab keeps. */
function mergeChanges(prev: WorkspaceChange[], updated: WorkspaceChange[]): WorkspaceChange[] {
  const byId = new Map(prev.map((c) => [c.id, c] as const));
  for (const u of updated) byId.set(u.id, u);
  return Array.from(byId.values());
}

function finiteOrFallback(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/** R32: the base version is never modified, but a finished record cannot be edited here either. One expression, one of four reasons. */
function readOnlyReasonOf(params: {
  editable: boolean;
  version: ContractsVersion | null;
  workspace: WorkspaceRow | null;
}): string | null {
  const { editable, version, workspace } = params;
  if (!editable) return 'Somebody else holds the edit lock.';
  if (version?.direction === 'sent') return 'This version was sent to the sponsor — it is an immutable record (R32).';
  if (version?.is_executed) return 'This version is executed — it is an immutable record (R32).';
  if (workspace && workspace.status !== 'open') return `This workspace is ${workspace.status} — the round is over (§8.6).`;
  return null;
}

function WorkspaceTabInner() {
  const { toast } = useToast();
  const { availableSites, isAdmin, isSuperAdmin } = useAuth();
  const { siteId: ambientSiteId } = useContracts();
  const [searchParams, setSearchParams] = useSearchParams();

  const workspaceIdParam = searchParams.get('workspaceId');
  const versionIdParam = searchParams.get('versionId');
  const modeParam = searchParams.get('mode') as WorkspaceMode | null;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [workspace, setWorkspace] = useState<WorkspaceRow | null>(null);
  const [lock, setLock] = useState<WorkspaceLockInfo | null>(null);
  const [holder, setHolder] = useState<WorkspaceHolder | null>(null);
  const [editable, setEditable] = useState(false);
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [counts, setCounts] = useState<WorkspaceCounts | null>(null);
  const [heartbeatSeconds, setHeartbeatSeconds] = useState(DEFAULT_HEARTBEAT_SECONDS);
  const [version, setVersion] = useState<ContractsVersion | null>(null);

  // THE SHEETS IDENTITY RULE — see this file's header. Never replaced from a
  // PUT /cells response.
  const [sheets, setSheets] = useState<GridSheet[] | null>(null);
  const [changes, setChanges] = useState<WorkspaceChange[]>([]);

  const [panelTab, setPanelTab] = useState<PanelTab>('changes');
  const [focusEvent, setFocusEvent] = useState<CellFocusEvent | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [breaking, setBreaking] = useState(false);

  const [rateCardOpen, setRateCardOpen] = useState(false);
  const [previousStudyOpen, setPreviousStudyOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [excelCheckPassed, setExcelCheckPassed] = useState<Record<ExportPathMode, boolean>>({
    edit_in_place: false,
    own_version: false,
  });

  const gridRef = useRef<BudgetGridHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [gridSize, setGridSize] = useState({ width: FALLBACK_WIDTH, height: FALLBACK_HEIGHT });

  const loadedWorkspaceIdRef = useRef<string | null>(null);
  const editableRef = useRef(false);
  useEffect(() => {
    editableRef.current = editable;
  }, [editable]);

  // ---------------------------------------------------------------------
  // jsdom has no layout and react-window measures nothing (WP-50 README):
  // measure the container, fall back to 960x420 whenever the measurement is
  // 0, NaN or non-finite.
  // ---------------------------------------------------------------------
  useEffect(() => {
    function measure() {
      const w = containerRef.current?.offsetWidth ?? 0;
      const h = window.innerHeight - 320; // real chrome: app bar, page header, toolbar, padding
      setGridSize({ width: finiteOrFallback(w, FALLBACK_WIDTH), height: finiteOrFallback(h, FALLBACK_HEIGHT) });
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  function seedFromView(
    view: {
      workspace: WorkspaceRow;
      lock: WorkspaceLockInfo;
      holder: WorkspaceHolder | null;
      editable: boolean;
      summary: WorkspaceSummary;
      counts: WorkspaceCounts;
      heartbeat_seconds?: number;
    },
    opts: { seedSheets: boolean }
  ) {
    setWorkspace(view.workspace);
    setLock(view.lock);
    setHolder(view.holder);
    setEditable(view.editable);
    setSummary(view.summary);
    setCounts(view.counts);
    if (view.heartbeat_seconds) setHeartbeatSeconds(view.heartbeat_seconds);
    if (opts.seedSheets) setSheets(view.workspace.grid.sheets);
  }

  // ---------------------------------------------------------------------
  // Load: ?workspaceId= directly, or ?versionId=&mode= opens/re-opens then
  // rewrites the query so a reload is idempotent.
  // ---------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    async function loadFromWorkspaceId(id: string) {
      setLoading(true);
      setLoadError(null);
      try {
        const view: WorkspaceGetResult = await getWorkspace(id);
        if (cancelled) return;
        seedFromView(view, { seedSheets: true });
        loadedWorkspaceIdRef.current = id;
        // GET does not itself take the lock (workspace/README.md: read-only).
        // A free lock (nobody holds it) is acquired on the person's behalf so
        // opening a workspace directly by URL behaves like opening it fresh.
        if (!view.editable && !view.lock.held) {
          try {
            const acquired = await lockWorkspace(id);
            if (!cancelled) {
              setLock(acquired.lock);
              setHolder(acquired.holder);
              setEditable(acquired.editable);
            }
          } catch {
            // 423: somebody took it between the GET and now. Leave the GET's view standing.
          }
        }
        const [v, chg] = await Promise.all([getVersion(view.workspace.base_version_id), listWorkspaceChanges(id)]);
        if (cancelled) return;
        setVersion(v);
        setChanges(chg.changes);
      } catch (err) {
        if (!cancelled) setLoadError(describeWorkspaceApiError('GET', `/api/contracts/workspaces/${id}`, err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    async function openFromVersion(vId: string, m: WorkspaceMode) {
      setLoading(true);
      setLoadError(null);
      try {
        const opened: WorkspaceOpenResult = await openWorkspace({ versionId: vId, mode: m });
        if (cancelled) return;
        seedFromView(opened, { seedSheets: true });
        loadedWorkspaceIdRef.current = opened.workspace.id;
        const [v, chg] = await Promise.all([
          getVersion(opened.workspace.base_version_id),
          listWorkspaceChanges(opened.workspace.id),
        ]);
        if (cancelled) return;
        setVersion(v);
        setChanges(chg.changes);
        setSearchParams({ workspaceId: opened.workspace.id }, { replace: true });
      } catch (err) {
        if (!cancelled) setLoadError(describeWorkspaceApiError('POST', '/api/contracts/workspaces', err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (workspaceIdParam) {
      if (loadedWorkspaceIdRef.current !== workspaceIdParam) {
        loadFromWorkspaceId(workspaceIdParam);
      }
    } else if (versionIdParam && modeParam) {
      openFromVersion(versionIdParam, modeParam);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceIdParam, versionIdParam, modeParam]);

  const reseedAfterServerChange = useCallback(
    async (message?: string) => {
      if (!workspaceIdParam) return;
      try {
        const view = await getWorkspace(workspaceIdParam);
        seedFromView(view, { seedSheets: true });
        toast(message ?? 'Working copy reloaded; in-grid undo history cleared.', { type: 'info' });
      } catch {
        // Best-effort: the action that triggered this already reported its own result.
      }
    },
    [workspaceIdParam, toast]
  );

  // ---------------------------------------------------------------------
  // The lock — POLLING (see this file's header). One 30 s interval.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!workspaceIdParam) return undefined;
    const intervalMs = Math.max(1, heartbeatSeconds) * 1000;
    const tick = async () => {
      try {
        if (editableRef.current) {
          const view = await heartbeatWorkspace(workspaceIdParam);
          setLock(view.lock);
          setHolder(view.holder);
          setEditable(view.editable);
        } else {
          // GET ignores workspace.grid entirely (identity rule) — only lock/holder/editable/counts are read off it.
          const view = await getWorkspace(workspaceIdParam);
          setLock(view.lock);
          setHolder(view.holder);
          setEditable(view.editable);
          setCounts(view.counts);
        }
      } catch {
        // A transient poll failure leaves the last known state on screen.
      }
    };
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [workspaceIdParam, heartbeatSeconds, editable]);

  // Release the lock on unmount and on page unload (§8.5).
  useEffect(() => {
    if (!workspaceIdParam) return undefined;
    const id = workspaceIdParam;
    const release = () => {
      if (!editableRef.current) return;
      unlockWorkspace(id).catch(() => {});
    };
    window.addEventListener('beforeunload', release);
    return () => {
      window.removeEventListener('beforeunload', release);
      release();
    };
  }, [workspaceIdParam]);

  // ---------------------------------------------------------------------
  // The change stream: buffer + debounce ~600ms, flush immediately on a
  // structural insertion (WP-51 README: "no column removed event" — send
  // the fresh sheets on any batch that inserted).
  // ---------------------------------------------------------------------
  const pendingRef = useRef<GridChangeEvent[]>([]);
  const structuralRef = useRef(false);
  const flushTimerRef = useRef<number | null>(null);

  // §16.7 (WP-57): the client key and the bounded retry. flushRef mints the
  // key at the freeze (THE CLIENT KEY); frozenRef retains the payload of a
  // sequence that ended in failure, so a manual Retry — or R-4's
  // reference-identity check on the next freeze — has something to compare
  // against and resend; attemptRef counts sends within the CURRENT frozen
  // payload only, reset on a genuinely fresh freeze; inFlightRef is non-null
  // for the WHOLE outstanding sequence (every wait between attempts
  // included, R-5), which is what makes scheduleFlush(0)/(600) firing
  // mid-retry a no-op rather than a second parallel PUT; retryTimerRef is
  // the schedule's own timer, distinct from flushTimerRef's debounce timer;
  // mountedRef guards every setState and every further schedule against a
  // dead tree (R-9).
  const flushRef = useRef(0);
  const frozenRef = useRef<FrozenFlushPayload | null>(null);
  const attemptRef = useRef(0);
  const inFlightRef = useRef<string | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  // R-6: the persistent notice a failed sequence leaves up, replacing the
  // silent drop this WP closes. Set only when a sequence ends in failure;
  // cleared only by a success (a `replayed: true` response included, R-7).
  const [unsaved, setUnsaved] = useState<{ reason: string } | null>(null);

  // R-9: clears both of this block's timers and marks the tab unmounted, so
  // neither an in-flight schedule nor a debounce fired after unmount can call
  // setState on a dead tree.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
      if (retryTimerRef.current !== null) window.clearTimeout(retryTimerRef.current);
    };
  }, []);

  const flush = useCallback(async () => {
    if (flushTimerRef.current !== null) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    // R-5: a sequence already on the wire (including its retry waits) runs
    // to its own end; this is what keeps a second parallel PUT from ever
    // starting, whether this call came from the 600ms debounce, a
    // structural insertion's scheduleFlush(0), or a manual Retry click.
    if (inFlightRef.current !== null) return;
    if (!workspaceIdParam) return;

    const toSend = pendingRef.current;
    const retained = frozenRef.current;
    // R-4: reuse the retained payload WHOLE — same key, same array, same
    // already-taken sheets snapshot — only when the event list is
    // reference-identical to what a failed sequence left behind (never a
    // deep or value compare: queueChange appends the grid's own event
    // objects and never mutates one). Anything else — one more event
    // appended, or nothing retained at all — mints a new key and freezes
    // afresh.
    const reusable = retained !== null
      && retained.events.length === toSend.length
      && retained.events.every((event, index) => event === toSend[index]);

    let payload: FrozenFlushPayload;
    if (reusable && retained) {
      payload = retained;
    } else {
      if (toSend.length === 0) return;
      flushRef.current += 1;
      const includeSheets = structuralRef.current;
      // R-2: capture-and-clear AT THE FREEZE, before the first await — a
      // fresh freeze only; the reuse branch above touches this flag not at
      // all, so a flag raised since the last failure still travels with
      // THIS new payload rather than being consumed by a resend that does
      // not carry its edits.
      structuralRef.current = false;
      payload = {
        clientBatchId: `flush-${Date.now().toString(36)}-${flushRef.current}`,
        events: toSend,
        includeSheets,
        sheets: includeSheets && gridRef.current ? gridRef.current.getSheets() : undefined,
      };
      attemptRef.current = 0;
    }

    inFlightRef.current = payload.clientBatchId;

    const attemptOnce = async (): Promise<void> => {
      attemptRef.current += 1;
      try {
        const result = await putWorkspaceCells(workspaceIdParam, {
          changes: payload.events,
          sheets: payload.sheets,
          clientBatchId: payload.clientBatchId,
        });
        if (!mountedRef.current) return;
        // R-2: remove exactly the events SENT, by reference identity — the
        // buffer may have grown while the request was out.
        const sent = new Set(payload.events);
        pendingRef.current = pendingRef.current.filter((event) => !sent.has(event));
        frozenRef.current = null;
        inFlightRef.current = null;
        setChanges((prev) => mergeChanges(prev, result.changes));
        setCounts(result.counts);
        setUnsaved(null);
        // Identity rule: NEVER set `sheets` from this response. A
        // `replayed: true` answer is treated exactly like an ordinary
        // success (R-7): the id-keyed merge above collapses a row arriving
        // twice onto one entry.
        if (pendingRef.current.length > 0) {
          // The same "flush at 0ms" shape scheduleFlush(0) uses — inlined
          // here rather than calling scheduleFlush itself (declared later
          // in this file, forbidden to touch): the buffer that grew while
          // this sequence was outstanding goes out next, under a fresh key
          // (R-5).
          if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
          flushTimerRef.current = window.setTimeout(() => {
            flushTimerRef.current = null;
            void flush();
          }, 0);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        // R-2: a flag raised while this request was out must survive —
        // never cleared on success, only ever OR-restored here.
        structuralRef.current = structuralRef.current || payload.includeSheets;
        frozenRef.current = payload;

        // R-3, on the 423 specifically: take the refusal's own news rather
        // than waiting up to 30s for the lock poll. This module never reads
        // a status off the rejection itself — api/workspace.ts does, and
        // this helper's own null-when-not-423 shape IS that check.
        const holder = lockHolderFromError(err);
        if (holder) {
          setHolder(holder);
          setEditable(false);
        }

        if (isRetryableWorkspaceError(err) && attemptRef.current < FLUSH_MAX_ATTEMPTS) {
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            if (mountedRef.current) void attemptOnce();
          }, FLUSH_RETRY_DELAYS_MS[attemptRef.current - 1]);
          return;
        }

        // Either not worth resending, or the schedule is exhausted: the
        // person is told AT ONCE with a persistent notice (R-6), never a
        // toast that scrolls away — plus ONE toast for this whole sequence.
        inFlightRef.current = null;
        const reason = describeWorkspaceApiError('PUT', `/api/contracts/workspaces/${workspaceIdParam}/cells`, err);
        setUnsaved({ reason });
        toast(reason, { type: 'error' });
      }
    };

    await attemptOnce();
  }, [workspaceIdParam, toast]);

  const scheduleFlush = useCallback(
    (delayMs: number) => {
      if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        flush();
      }, delayMs);
    },
    [flush]
  );

  const queueChange = useCallback(
    (event: GridChangeEvent) => {
      pendingRef.current = [...pendingRef.current, event];
      scheduleFlush(600);
    },
    [scheduleFlush]
  );

  const queueInsertColumn = useCallback(
    (_event: InsertColumnEvent) => {
      structuralRef.current = true;
      scheduleFlush(0);
    },
    [scheduleFlush]
  );

  const queueInsertRow = useCallback(
    (_event: InsertRowEvent) => {
      structuralRef.current = true;
      scheduleFlush(0);
    },
    [scheduleFlush]
  );

  const handleCellFocus = useCallback((event: CellFocusEvent) => {
    setFocusEvent(event);
  }, []);

  const notesSelection: NotesPanelSelection | undefined = useMemo(() => {
    if (!focusEvent) return undefined;
    const provenanceValue = focusEvent.cell?.provenance;
    const provenance = provenanceValue && typeof provenanceValue === 'object' ? (provenanceValue as Record<string, unknown>) : null;
    const budgetLineId = provenance && typeof provenance.budget_line_id === 'string' ? provenance.budget_line_id : undefined;
    return {
      sheet: focusEvent.sheet,
      cellRef: focusEvent.cell_ref,
      cellId: typeof focusEvent.cell?.id === 'number' ? focusEvent.cell.id : undefined,
      budgetLineId,
    };
  }, [focusEvent]);

  const readOnlyReason = readOnlyReasonOf({ editable, version, workspace });
  const readOnly = readOnlyReason !== null;

  function focusChange(change: WorkspaceChange) {
    const parsed = parseCellRef(change.cell_ref);
    if (!parsed) return;
    gridRef.current?.setActiveSheet(change.sheet);
    gridRef.current?.focusCell({ sheet: change.sheet, row: parsed.row, col: parsed.col });
  }

  async function handleAcceptIds(ids: string[]) {
    if (!workspaceIdParam) return;
    try {
      const result = await acceptWorkspaceChanges(workspaceIdParam, { ids });
      setChanges((prev) => mergeChanges(prev, result.changes));
      setCounts(result.counts);
      await reseedAfterServerChange();
    } catch (err) {
      const message = describeWorkspaceApiError('POST', `/api/contracts/workspaces/${workspaceIdParam}/changes/accept`, err);
      toast(message, { type: 'error' });
    }
  }

  async function handleRejectIds(ids: string[]) {
    if (!workspaceIdParam) return;
    try {
      const result = await rejectWorkspaceChanges(workspaceIdParam, { ids });
      setChanges((prev) => mergeChanges(prev, result.changes));
      setCounts(result.counts);
    } catch (err) {
      const message = describeWorkspaceApiError('POST', `/api/contracts/workspaces/${workspaceIdParam}/changes/reject`, err);
      toast(message, { type: 'error' });
    }
  }

  async function handleAcceptAll() {
    if (!workspaceIdParam) return;
    setAccepting(true);
    try {
      const result = await acceptWorkspaceChanges(workspaceIdParam, { all: true });
      setChanges((prev) => mergeChanges(prev, result.changes));
      setCounts(result.counts);
      toast(`Accepted ${result.applied} change${result.applied === 1 ? '' : 's'}.`, { type: 'success' });
      await reseedAfterServerChange();
    } catch (err) {
      const message = describeWorkspaceApiError('POST', `/api/contracts/workspaces/${workspaceIdParam}/changes/accept`, err);
      setLoadError(message);
      toast(message, { type: 'error' });
    } finally {
      setAccepting(false);
    }
  }

  function handleRateCardApplied(result: ApplyRateCardResult) {
    setChanges((prev) => mergeChanges(prev, result.proposals));
    setPanelTab('changes');
    void reseedAfterServerChange();
    const extra = result.unmatched.length > 0 || result.skipped.length > 0
      ? `, ${result.unmatched.length} unmatched, ${result.skipped.length} skipped`
      : '';
    toast(`Apply rate card: ${result.proposals.length} proposed${extra}.`, { type: 'success' });
  }

  function handlePreviousStudyApplied(result: ApplyPreviousStudyResult) {
    setChanges((prev) => mergeChanges(prev, result.proposals));
    setPanelTab('changes');
    void reseedAfterServerChange();
    toast(`Apply previous study: ${result.proposals.length} proposed, ${result.unmatched.length} unmatched.`, { type: 'success' });
  }

  function handleExported(pathMode: ExportPathMode, result: ExportWorkspaceResult) {
    if (result.excelCheck === 'passed') {
      setExcelCheckPassed((prev) => ({ ...prev, [pathMode]: true }));
    }
    void reseedAfterServerChange('Exported — the workspace was reloaded.');
    toast(`Exported as version #${result.version?.version_no ?? '—'}.`, { type: result.excelCheck === 'failed' ? 'error' : 'success' });
  }

  function handleDialogError(message: string) {
    toast(message, { type: 'error' });
  }

  async function handleBreakLock() {
    if (!workspaceIdParam) return;
    setBreaking(true);
    try {
      const view = await unlockWorkspace(workspaceIdParam, { force: true });
      setLock(view.lock);
      setHolder(view.holder);
      setEditable(view.editable);
      toast('Lock broken (audited).', { type: 'info' });
    } catch (err) {
      toast(describeWorkspaceApiError('DELETE', `/api/contracts/workspaces/${workspaceIdParam}/lock`, err), { type: 'error' });
    } finally {
      setBreaking(false);
    }
  }

  async function handleReload() {
    if (!workspaceIdParam) return;
    setLoading(true);
    try {
      const view = await getWorkspace(workspaceIdParam);
      seedFromView(view, { seedSheets: true });
      const chg = await listWorkspaceChanges(workspaceIdParam);
      setChanges(chg.changes);
      const v = await getVersion(view.workspace.base_version_id);
      setVersion(v);
      setLoadError(null);
    } catch (err) {
      setLoadError(describeWorkspaceApiError('GET', `/api/contracts/workspaces/${workspaceIdParam}`, err));
    } finally {
      setLoading(false);
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!workspaceIdParam && !(versionIdParam && modeParam)) {
    return <WorkspaceOpener onOpened={(id) => setSearchParams({ workspaceId: id }, { replace: true })} />;
  }

  if (loading && !workspace) {
    return (
      <div className="mx-auto flex w-full max-w-[1700px] items-center gap-2 p-4 text-sm text-gray-500 sm:p-6 lg:p-8 dark:text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Loading workspace&hellip;
      </div>
    );
  }

  if (loadError && !workspace) {
    return (
      <div className="mx-auto w-full max-w-[1700px] p-4 sm:p-6 lg:p-8">
        <EmptyState icon={LayoutGrid} title="Could not open the workspace" hint={loadError} variant="card" />
      </div>
    );
  }

  if (!workspace) return null;

  const proposedCount = counts?.proposed ?? 0;
  const canBreakLock = isAdmin || isSuperAdmin;
  const subtitle = version
    ? `Version #${version.version_no} — ${version.direction} — ${version.version_date} — mode: ${workspace.mode === 'edit_in_place' ? 'edit in place' : "WCR's own version"}`
    : undefined;

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-3 p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Workspace"
        icon={LayoutGrid}
        subtitle={subtitle}
        actions={
          <>
            {readOnly && (
              <span
                data-testid="ws-readonly"
                className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                title={readOnlyReason ?? undefined}
              >
                Read-only
              </span>
            )}
            <LockIndicator lock={lock} holder={holder} editable={editable} canBreakLock={canBreakLock} breaking={breaking} onBreakLock={handleBreakLock} />
          </>
        }
      />

      <Toolbar
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        proposedCount={proposedCount}
        accepting={accepting}
        excelCheckPassed={excelCheckPassed}
        onApplyRateCard={() => setRateCardOpen(true)}
        onApplyPreviousStudy={() => setPreviousStudyOpen(true)}
        onAcceptAll={handleAcceptAll}
        onExport={() => setExportOpen(true)}
        onReload={handleReload}
      />

      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}

      {unsaved && (
        <div
          data-testid="ws-unsaved-notice"
          role="alert"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Your recent edits could not be saved: {unsaved.reason}</span>
          <button
            type="button"
            data-testid="ws-unsaved-retry"
            className={outlineBtnCls()}
            onClick={() => {
              void flush();
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3 lg:flex-row">
        <div ref={containerRef} className="min-w-0 flex-1">
          {sheets && (
            <BudgetGrid
              ref={gridRef}
              sheets={sheets}
              changes={changes}
              readOnly={readOnly}
              width={gridSize.width}
              height={gridSize.height}
              onChange={queueChange}
              onInsertColumn={queueInsertColumn}
              onInsertRow={queueInsertRow}
              onCellFocus={handleCellFocus}
            />
          )}
        </div>
        <div className="w-full shrink-0 lg:w-[360px] xl:w-[420px]">
          <SidePanel
            panelTab={panelTab}
            onPanelTabChange={setPanelTab}
            proposedCount={proposedCount}
            changesPanelProps={{
              changes,
              readOnly,
              onFocusCell: focusChange,
              onAccept: handleAcceptIds,
              onReject: handleRejectIds,
            }}
            negotiationId={workspace.negotiation_id}
            versionId={workspace.base_version_id}
            notesSelection={notesSelection}
            readOnly={readOnly}
          />
        </div>
      </div>

      <ApplyRateCardDialog
        isOpen={rateCardOpen}
        onClose={() => setRateCardOpen(false)}
        workspaceId={workspace.id}
        ambientSiteId={ambientSiteId}
        sites={availableSites.map((s) => ({ id: s.id, name: s.name }))}
        onApplied={handleRateCardApplied}
        onError={handleDialogError}
      />
      <ApplyPreviousStudyDialog
        isOpen={previousStudyOpen}
        onClose={() => setPreviousStudyOpen(false)}
        workspaceId={workspace.id}
        onApplied={handlePreviousStudyApplied}
        onError={handleDialogError}
      />
      <ExportDialog
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
        workspaceId={workspace.id}
        negotiationId={workspace.negotiation_id}
        excelCheckPassed={excelCheckPassed}
        onExported={handleExported}
        onError={handleDialogError}
      />
    </div>
  );
}

export function WorkspaceTab() {
  return (
    <ToastProvider>
      <WorkspaceTabInner />
    </ToastProvider>
  );
}

export default WorkspaceTab;
